import { createBrowserRouter, Navigate } from 'react-router';

import { AcceptInvitePage } from '@/app/auth/accept-invite-page';
import { ForgotPasswordPage } from '@/app/auth/forgot-password-page';
import { ResetPasswordPage } from '@/app/auth/reset-password-page';
import { VerifyEmailPage } from '@/app/auth/verify-email-page';
import { SignInPage } from '@/app/auth/sign-in-page';
import { SignUpPage } from '@/app/auth/sign-up-page';
import { BillingPage as SubscriptionBillingPage } from '@/features/admin/billing-page';
import { RolesPage } from '@/features/admin/roles-page';
import { ImportExportPage } from '@/features/admin/impexp-page';
import { IntegrationsPage } from '@/features/admin/integrations-page';
import { NumberSequencesPage } from '@/features/admin/number-sequences-page';
import { PaymentTermsPage } from '@/features/admin/payment-terms-page';
import { PrintSettingsPage } from '@/features/admin/print-settings-page';
import { SettingsPage } from '@/features/admin/settings-page';
import { NotificationsPage } from '@/features/notifications/notifications-page';
import { SearchPage } from '@/features/search/search-page';
import { HelpPage } from '@/features/help/help-page';
import { PersonalizationPage } from '@/features/personalization/personalization-page';
import { UsersPage } from '@/features/admin/users-page';
import { PublicOnly, RequireAuth, RequirePermission } from '@/app/guards';
import { GroupPage } from '@/app/pages/group-page';
import { AppShell } from '@/app/shell/app-shell';
import { PosPage } from '@/features/pos/pos-page';
import { ImportNewPage } from '@/features/purchases/import-new-page';
import { ImportOrderPage } from '@/features/purchases/import-order-page';
import { ImportsListPage } from '@/features/purchases/imports-list-page';
import { AdjustmentEditorPage } from '@/features/inventory/adjustment-editor-page';
import { AdjustmentsListPage } from '@/features/inventory/adjustments-list-page';
import { CategoriesPage } from '@/features/inventory/categories-page';
import { CountEditorPage } from '@/features/inventory/count-editor-page';
import { CountsListPage } from '@/features/inventory/counts-list-page';
import { ItemFormPage } from '@/features/inventory/item-form-page';
import { ItemsListPage } from '@/features/inventory/items-list-page';
import { ManufacturersPage } from '@/features/inventory/manufacturers-page';
import { StockLevelsPage } from '@/features/inventory/stock-levels-page';
import { StockMovementsPage } from '@/features/inventory/stock-movements-page';
import { TiersPage } from '@/features/inventory/tiers-page';
import { TransferEditorPage } from '@/features/inventory/transfer-editor-page';
import { TransfersListPage } from '@/features/inventory/transfers-list-page';
import { UnitsPage } from '@/features/inventory/units-page';
import { WarehousesPage } from '@/features/inventory/warehouses-page';
import { BomPage } from '@/features/manufacturing/bom-page';
import { ConsumptionPage } from '@/features/manufacturing/consumption-page';
import { PlanningPage } from '@/features/manufacturing/planning-page';
import { WorkOrderPage } from '@/features/manufacturing/work-order-page';
import { WorkOrdersListPage } from '@/features/manufacturing/work-orders-list-page';
import { ChartPage } from '@/features/accounts/chart-page';
import { CurrenciesPage } from '@/features/fx/currencies-page';
import { AssetsPage } from '@/features/fixed-assets/assets-page';
import { EinvoicingPage } from '@/features/einvoicing/einvoicing-page';
import { JournalEditorPage } from '@/features/journals/journal-editor-page';
import { JournalsListPage } from '@/features/journals/journals-list-page';
import { PeriodsPage } from '@/features/periods/periods-page';
import { FollowupsPage } from '@/features/crm/followups-page';
import { LeadsPage } from '@/features/crm/leads-page';
import { OpportunitiesPage } from '@/features/crm/opportunities-page';
import { PartnerDetailPage } from '@/features/partners/partner-detail-page';
import { PartnerFormPage } from '@/features/partners/partner-form-page';
import { PartnerGroupsPage } from '@/features/partners/partner-groups-page';
import { PartnersListPage } from '@/features/partners/partners-list-page';
import { PaymentEditorPage } from '@/features/payments/payment-editor-page';
import { PaymentsListPage } from '@/features/payments/payments-list-page';
import { BillEditorPage } from '@/features/purchases/bill-editor-page';
import { BillsListPage } from '@/features/purchases/bills-list-page';
import { OrderEditorPage as PurchaseOrderEditorPage } from '@/features/purchases/order-editor-page';
import { OrdersListPage } from '@/features/purchases/orders-list-page';
import { ReceiptEditorPage } from '@/features/receipts/receipt-editor-page';
import { ReorderPage } from '@/features/purchases/reorder-page';
import { PurchaseReturnEditorPage } from '@/features/purchases/return-editor-page';
import { PurchaseReturnsListPage } from '@/features/purchases/returns-list-page';
import { ReceiptEditorPage as GrnEditorPage } from '@/features/purchases/receipt-editor-page';
import { ReceiptsListPage as GrnListPage } from '@/features/purchases/receipts-list-page';
import { ReceiptsListPage } from '@/features/receipts/receipts-list-page';
import { InvoiceEditorPage } from '@/features/sales/invoice-editor-page';
import { InvoicesListPage } from '@/features/sales/invoices-list-page';
import { OrderEditorPage } from '@/features/sales/order-editor-page';
import { OrdersPage } from '@/features/sales/orders-page';
import { QuoteEditorPage } from '@/features/sales/quote-editor-page';
import { QuotesListPage } from '@/features/sales/quotes-list-page';
import { DeliveriesListPage } from '@/features/sales/deliveries-list-page';
import { DeliveryEditorPage } from '@/features/sales/delivery-editor-page';
import { ReturnEditorPage } from '@/features/sales/return-editor-page';
import { ReturnsListPage } from '@/features/sales/returns-list-page';
import { BankAccountsPage } from '@/features/banking/bank-accounts-page';
import { AttendancePage } from '@/features/hr/attendance-page';
import { EmployeesPage } from '@/features/hr/employees-page';
import { JobRolesPage } from '@/features/hr/job-roles-page';
import { LeavePage } from '@/features/hr/leave-page';
import { PayrollPage } from '@/features/hr/payroll-page';
import { RecruitmentPage } from '@/features/hr/recruitment-page';
import { BillingPage } from '@/features/projects/billing-page';
import { ProjectsPage } from '@/features/projects/projects-page';
import { TasksPage } from '@/features/projects/tasks-page';
import { TimesheetsPage } from '@/features/projects/timesheets-page';
import { CashflowPage } from '@/features/dashboards/cashflow-page';
import { FinancialDashboardPage } from '@/features/dashboards/financial-dashboard-page';
import { InventoryDashboardPage } from '@/features/dashboards/inventory-dashboard-page';
import { SalesDashboardPage } from '@/features/dashboards/sales-dashboard-page';
import { AuditLogPage } from '@/features/audit/audit-log-page';
import { FinancialReportPage } from '@/features/reports/financial-report-page';
import { StatementReportPage } from '@/features/reports/statement-report-page';
import { InventoryReportPage } from '@/features/reports/inventory-report-page';
import { PurchaseReportPage } from '@/features/reports/purchase-report-page';
import { SalesReportPage } from '@/features/reports/sales-report-page';
import { BankOperationsPage } from '@/features/banking/overview-page';
import { ChecksPage } from '@/features/banking/checks-page';
import { TransferEditorPage as CashTransferEditorPage } from '@/features/banking/transfer-editor-page';
import { TransfersListPage as CashTransfersListPage } from '@/features/banking/transfers-list-page';

/**
 * The route table. DECISION.md §2 names no SPA router; React Router (data
 * mode) is used as the mature default of that ecosystem — the URL carries
 * filters and pagination later (§2.6), which is plain route/search-param work
 * either way.
 *
 * Public: GEN-S1 /sign-in and GEN-S2 /sign-up — the two screens that exist
 * before the menu does. Everything else lives inside the shell behind
 * RequireAuth, and every unknown path funnels to the default landing screen.
 */
export function createAppRouter() {
  return createBrowserRouter([
    {
      path: '/sign-in',
      element: (
        <PublicOnly>
          <SignInPage />
        </PublicOnly>
      ),
    },
    {
      path: '/sign-up',
      element: (
        <PublicOnly>
          <SignUpPage />
        </PublicOnly>
      ),
    },
    {
      // The activation link (ADM-05 §7) — reachable with or without a
      // session: the token, not the cookie, is the credential.
      path: '/accept-invite',
      element: <AcceptInvitePage />,
    },
    {
      // GEN-S1 "Forgot password?" — the flow the sign-in screen has been
      // drawing a link to since the beginning.
      path: '/forgot-password',
      element: (
        <PublicOnly>
          <ForgotPasswordPage />
        </PublicOnly>
      ),
    },
    {
      // The link out of the reset email. Not PublicOnly: somebody signed in
      // on another device may well be the person resetting, and bouncing them
      // to the shell would strand the link.
      path: '/reset-password',
      element: <ResetPasswordPage />,
    },
    {
      // BR-GEN-S2-08/09's activation link. Same reasoning as above.
      path: '/verify-email',
      element: <VerifyEmailPage />,
    },
    {
      /**
       * SAL-06 — the till, and the ONE route that is authenticated but sits
       * OUTSIDE the shell. "A standalone full-screen application with its own
       * route and no dashboard shell — no sidebar, no top bar, no
       * breadcrumb." A counter screen with a menu down the side is a counter
       * screen a cashier can get lost in, so the separation is structural
       * rather than a matter of styling.
       */
      path: '/pos',
      element: (
        <RequireAuth>
          {/* The till has no menu entry to hide (it is outside the shell), so
              this wrapper is the only place BR-PERM-51's client half can be
              said for it. Area `sales`, per route-permissions.ts. */}
          <RequirePermission>
            <PosPage />
          </RequirePermission>
        </RequireAuth>
      ),
    },
    {
      path: '/',
      element: (
        <RequireAuth>
          <AppShell />
        </RequireAuth>
      ),
      children: [
        // PART 14 — the Home group. '/' is the default landing screen
        // (GEN-2a): DSH-01 Financial Dashboard, the FIXED board with no
        // customization. The other three are the R1 customizable boards,
        // one saved layout per user per board.
        { index: true, element: <FinancialDashboardPage /> },
        { path: 'home/sales', element: <SalesDashboardPage /> },
        { path: 'home/inventory', element: <InventoryDashboardPage /> },
        { path: 'home/cashflow', element: <CashflowPage /> },
        // MD-01 — Business Partners, under the CRM menu group.
        { path: 'partners', element: <PartnersListPage /> },
        // PART 10 — the CRM screens join the same menu group: CRM-01 Leads,
        // CRM-02 Opportunities / Pipeline, CRM-03 Follow-ups. Static
        // segments outrank :partnerId, so these never collide with details.
        { path: 'partners/leads', element: <LeadsPage /> },
        { path: 'partners/opportunities', element: <OpportunitiesPage /> },
        { path: 'partners/followups', element: <FollowupsPage /> },
        { path: 'partners/new', element: <PartnerFormPage /> },
        // MD-01 field 4's list (MD-RULE-9) — reached from the partner form's
        // Group field and the list's Manage groups link.
        { path: 'partners/groups', element: <PartnerGroupsPage /> },
        { path: 'partners/:partnerId', element: <PartnerDetailPage /> },
        { path: 'partners/:partnerId/edit', element: <PartnerFormPage /> },
        // SAL-04 — Sales Invoices, its own screen on its own route
        // (BR-SAL-00-28).
        { path: 'sales', element: <InvoicesListPage /> },
        { path: 'sales/new', element: <InvoiceEditorPage /> },
        // SAL-01 — Quotations: own screen, revisioning per §9.
        { path: 'sales/quotes', element: <QuotesListPage /> },
        { path: 'sales/quotes/new', element: <QuoteEditorPage /> },
        { path: 'sales/quotes/:quotationId', element: <QuoteEditorPage /> },
        // SAL-02 — Sales Orders: the list-and-detail screen + the draft editor.
        { path: 'sales/orders', element: <OrdersPage /> },
        { path: 'sales/orders/new', element: <OrderEditorPage /> },
        { path: 'sales/orders/:orderId/edit', element: <OrderEditorPage /> },
        // SAL-03 — Delivery Notes: what actually left the warehouse, against
        // a confirmed order (the document-integration wave).
        { path: 'sales/deliveries', element: <DeliveriesListPage /> },
        { path: 'sales/deliveries/new', element: <DeliveryEditorPage /> },
        { path: 'sales/deliveries/:deliveryId', element: <DeliveryEditorPage /> },
        // SAL-05 — Sales Returns (credit notes), from a posted invoice.
        { path: 'sales/returns', element: <ReturnsListPage /> },
        { path: 'sales/returns/new', element: <ReturnEditorPage /> },
        { path: 'sales/returns/:creditNoteId', element: <ReturnEditorPage /> },
        { path: 'sales/:invoiceId', element: <InvoiceEditorPage /> },
        // The Phase-4 item master under Inventory & Warehouses: MD-03 items
        // (catalog + editor), MD-04 categories, MD-12 units, MD-14 tiers.
        { path: 'inventory', element: <ItemsListPage /> },
        { path: 'inventory/items/new', element: <ItemFormPage /> },
        { path: 'inventory/items/:itemId', element: <ItemFormPage /> },
        { path: 'inventory/categories', element: <CategoriesPage /> },
        { path: 'inventory/units', element: <UnitsPage /> },
        { path: 'inventory/tiers', element: <TiersPage /> },
        { path: 'inventory/manufacturers', element: <ManufacturersPage /> },
        // The Phase-4 stock core: balances (INV-05), the append-only ledger
        // (INV-06), locations (MD-08/INV-04), Stock In/Out vouchers (INV-08),
        // stock takes (INV-05 §5.7) and warehouse transfers (INV-07).
        { path: 'inventory/stock', element: <StockLevelsPage /> },
        { path: 'inventory/movements', element: <StockMovementsPage /> },
        { path: 'inventory/warehouses', element: <WarehousesPage /> },
        { path: 'inventory/adjustments', element: <AdjustmentsListPage /> },
        { path: 'inventory/adjustments/new', element: <AdjustmentEditorPage /> },
        { path: 'inventory/adjustments/:adjustmentId', element: <AdjustmentEditorPage /> },
        { path: 'inventory/counts', element: <CountsListPage /> },
        { path: 'inventory/counts/new', element: <CountEditorPage /> },
        { path: 'inventory/counts/:countId', element: <CountEditorPage /> },
        { path: 'inventory/transfers', element: <TransfersListPage /> },
        { path: 'inventory/transfers/new', element: <TransferEditorPage /> },
        { path: 'inventory/transfers/:transferId', element: <TransferEditorPage /> },
        // PART 9 — the Manufacturing group, behind the `manufacturing` paid
        // module (§9.1.2, the ONE gate above permissions) and the
        // `manufacturing` permission area (16.2.2 row 4): MFG-01 Bills of
        // Materials is the group's landing screen, MFG-02 Work Orders, MFG-03
        // Production Planning, MFG-04 Material Consumption.
        { path: 'manufacturing', element: <BomPage /> },
        { path: 'manufacturing/work-orders', element: <WorkOrdersListPage /> },
        { path: 'manufacturing/work-orders/:workOrderId', element: <WorkOrderPage /> },
        { path: 'manufacturing/planning', element: <PlanningPage /> },
        { path: 'manufacturing/consumption', element: <ConsumptionPage /> },
        // PART 7 — the Purchases group: PUR-01 orders and PUR-03 bills, each
        // its own screen on its own route (owner ruling R3, §7.7). The section
        // OPENS on Purchase Invoices (owner ruling 27 Sep 2026), so the bare
        // /purchases path renders the bills list and orders gets its own path.
        { path: 'purchases', element: <BillsListPage /> },
        { path: 'purchases/orders', element: <OrdersListPage /> },
        { path: 'purchases/orders/new', element: <PurchaseOrderEditorPage /> },
        { path: 'purchases/orders/:orderId', element: <PurchaseOrderEditorPage /> },
        // PUR-02 — Goods Received Notes: what actually arrived, and whether
        // it was any good (the document-integration wave).
        // PUR-06 — the shipments list and one shipment.
        { path: 'purchases/imports', element: <ImportsListPage /> },
        { path: 'purchases/imports/new', element: <ImportNewPage /> },
        { path: 'purchases/imports/:importId', element: <ImportOrderPage /> },
        // Wave 5A — the reorder builder: suggestions → a PUR-01 draft.
        { path: 'purchases/reorder', element: <ReorderPage /> },
        { path: 'purchases/receipts', element: <GrnListPage /> },
        { path: 'purchases/receipts/new', element: <GrnEditorPage /> },
        { path: 'purchases/receipts/:receiptId', element: <GrnEditorPage /> },
        { path: 'purchases/bills', element: <BillsListPage /> },
        { path: 'purchases/bills/new', element: <BillEditorPage /> },
        { path: 'purchases/bills/:billId', element: <BillEditorPage /> },
        // PUR-04 — Purchase Returns (debit notes), from a posted bill.
        { path: 'purchases/returns', element: <PurchaseReturnsListPage /> },
        { path: 'purchases/returns/new', element: <PurchaseReturnEditorPage /> },
        { path: 'purchases/returns/:returnId', element: <PurchaseReturnEditorPage /> },
        // FIN-03 — Receipt Vouchers, under the Accounting & Finance group.
        { path: 'accounting', element: <ReceiptsListPage /> },
        { path: 'accounting/receipts/new', element: <ReceiptEditorPage /> },
        { path: 'accounting/receipts/:receiptId', element: <ReceiptEditorPage /> },
        // FIN-04 — Payment Vouchers: an Accounting & Finance screen (its
        // canonical menu home, FIN-04 §3); the purchasing entry points reach
        // it from a bill's Record payment button carrying supplier + bill.
        { path: 'accounting/payments', element: <PaymentsListPage /> },
        { path: 'accounting/payments/new', element: <PaymentEditorPage /> },
        { path: 'accounting/payments/:paymentId', element: <PaymentEditorPage /> },
        // FIN-01 — Chart of Accounts: the tree with live posted balances,
        // account maintenance, and the ledger drill.
        { path: 'accounting/chart', element: <ChartPage /> },
        // FIN-02 — Journal Entry: the register and the line editor with the
        // draft → approval → post → reverse workflow.
        { path: 'accounting/journals', element: <JournalsListPage /> },
        { path: 'accounting/journals/new', element: <JournalEditorPage /> },
        { path: 'accounting/journals/:entryId', element: <JournalEditorPage /> },
        // Q-FIN-01-3 — Accounting Periods: the fiscal calendar, closing and
        // reopening. Area `accounting` (16.2.2 row 6 names fiscal periods).
        { path: 'accounting/periods', element: <PeriodsPage /> },
        // FIN-05 — Currency Exchange: the rate table, the real dated history
        // chart and the month-end revaluation. FIN-07 — Fixed Assets: the
        // register, the schedule, the depreciation run and disposal. Both
        // sit in the accounting area per 16.2.2 row 6.
        { path: 'accounting/currencies', element: <CurrenciesPage /> },
        { path: 'accounting/assets', element: <AssetsPage /> },
        // FIN-08 — E-Invoicing / Tax Compliance. Its MENU home is Accounting
        // & Finance (FIN-08 §3); its PERMISSION area is `sales` (PART 16.2.2
        // row 1, which names e-invoicing there and not under accounting).
        { path: 'accounting/einvoicing', element: <EinvoicingPage /> },
        // PART 11 §11.2 + FIN-06 — the Banks and Checks group (banking.*):
        // FIN-12 Bank Operations is the group's landing page; FIN-09 Bank
        // Accounts carries the reconciliation tab and receives the
        // dashboard's ?account=&tab=reconciliation deep link; FIN-10/FIN-11
        // are the two cheque registers; FIN-06 Cash Transfer lives here per
        // 16.2.2 row 7.
        { path: 'banks', element: <BankOperationsPage /> },
        { path: 'banks/accounts', element: <BankAccountsPage /> },
        { path: 'banks/checks/incoming', element: <ChecksPage direction="incoming" /> },
        { path: 'banks/checks/outgoing', element: <ChecksPage direction="outgoing" /> },
        { path: 'banks/transfers', element: <CashTransfersListPage /> },
        { path: 'banks/transfers/new', element: <CashTransferEditorPage /> },
        { path: 'banks/transfers/:transferId', element: <CashTransferEditorPage /> },
        // PART 12 — the Employees & HR group: HR-01 Employee List (+ the
        // R11 job-role management as its own tab), HR-03 Attendance, HR-04
        // Leave, HR-02 Payroll (payroll.view-fenced), HR-05 Recruitment.
        { path: 'employees', element: <EmployeesPage /> },
        { path: 'employees/roles', element: <JobRolesPage /> },
        { path: 'employees/attendance', element: <AttendancePage /> },
        { path: 'employees/leave', element: <LeavePage /> },
        { path: 'employees/payroll', element: <PayrollPage /> },
        { path: 'employees/recruitment', element: <RecruitmentPage /> },
        // PART 13 — the Projects group (projects.*): PRJ-01 Projects List,
        // PRJ-02 Tasks (the shared engine's project face), PRJ-03
        // Timesheets, PRJ-04 Project Billing.
        { path: 'projects', element: <ProjectsPage /> },
        { path: 'projects/tasks', element: <TasksPage /> },
        { path: 'projects/timesheets', element: <TimesheetsPage /> },
        { path: 'projects/billing', element: <BillingPage /> },
        // PART 14 — the Reports group, in menu order: DSH-05 Sales (the
        // group's landing page), DSH-06 Purchases, DSH-07 Inventory, DSH-08
        // Financial Reports (P&L, Balance Sheet, Cash Flow). The Audit Log
        // is the fifth entry and stays with the admin area (BR-PERM-52).
        { path: 'reports', element: <SalesReportPage /> },
        { path: 'reports/purchases', element: <PurchaseReportPage /> },
        { path: 'reports/inventory', element: <InventoryReportPage /> },
        { path: 'reports/financial', element: <FinancialReportPage /> },
        // CRM-03 — the partner statement as a report centre (crm.view).
        { path: 'reports/statement', element: <StatementReportPage /> },
        // DSH-09 — the Audit Log: "the fifth and last entry in the Reports
        // group". Its MENU home is Reports; its PERMISSION is
        // admin.read_audit_log, its own grant (BR-DSH-09-13, BR-PERM-52), so
        // route-permissions.ts carries an exception for this path.
        { path: 'reports/audit', element: <AuditLogPage /> },
        // PART 15 — the admin wave. ADM-01 under General Settings; ADM-05
        // Users and ADM-06 Roles & Permissions under Admin (clicking the
        // parent group opens its first child — Users).
        // PART 15 ADM-04 — Notifications. Reached from the BELL, which every
        // account has whatever its role (§3, §9): the routes behind it are
        // @SelfService, so this path deliberately has no entry in
        // route-permissions.ts's exception list and falls through to the
        // session-only floor. The spec's second door — "Main menu › General
        // Settings › Notifications, which needs `admin` View" — is the
        // Settings group's own tab, and it points here.
        { path: 'notifications', element: <NotificationsPage /> },
        /*
         * §4.3.2 GEN-12 — the full results screen behind "See all N results".
         * Like the two paths below it, it carries NO permission of its own and
         * so has no entry in route-permissions.ts: the top bar draws the
         * search box for every signed-in account, and the RESULTS are what is
         * fenced (GEN-12a), arm by arm, on the server. A route gate here would
         * refuse somebody a screen that would have shown them exactly the
         * records they may open — possibly none, which is a legitimate and
         * self-explaining answer.
         */
        { path: 'search', element: <SearchPage /> },
        // PART 15 ADM-08 — Personalization. Reached from the SLIDERS BUTTON in
        // the top bar and from no menu entry at all (§3). It carries NO
        // permission (§9, BR-PERM-08: "Personalization has no permission at
        // all"), so it has no entry in route-permissions.ts and falls through
        // to the session-only floor — which is what makes BR-PERM-08
        // satisfiable for a role-less account (PART 16.1 rung 4).
        { path: 'personalization', element: <PersonalizationPage /> },
        // UB-03 — Help. Reached from the top bar's Help and Contact buttons
        // (`features/help/help-links.ts` composes `/help#<section>` for the
        // module the person is in). No permission, like the two personal
        // screens above it: help belongs to every signed-in account. The
        // screen itself is code-split behind this thin page (`help-page.tsx`).
        { path: 'help', element: <HelpPage /> },
        { path: 'settings', element: <SettingsPage /> },
        // ADM-03 — the Import / Export Centre. §3: "Main menu › General
        // Settings › Import / Export Centre — The Owner, and a role holding
        // the `admin` area", which is the Settings group's own gate, so no
        // route-permissions exception is needed.
        { path: 'settings/import-export', element: <ImportExportPage /> },
        // ADM-02 §3: "Main menu › General Settings › Integrations & API Keys —
        // The Owner, and a role holding the `admin` area", which is the
        // Settings group's own gate, so no route-permissions exception.
        { path: 'settings/integrations', element: <IntegrationsPage /> },
        // MD-13 §3: "Main menu > General Settings > Number sequences". The
        // route inherits the Settings group's `admin` gate for OPENING it;
        // the narrower admin.number_sequences is what Edit needs, and that is
        // asserted in the service, not here.
        { path: 'settings/number-sequences', element: <NumberSequencesPage /> },
        // MD-09 §3: "Main menu › General Settings › Payment terms". The API
        // is `accounting.*` (§2: the Accountant's screen), so the route gate
        // is the accounting area — route-permissions.ts carries the entry.
        { path: 'settings/payment-terms', element: <PaymentTermsPage /> },
        // INV-H6 / UB-03 — Printing: the eleven `print.*` company settings
        // with a live preview. Reached from the top bar's Print options button
        // and the Settings tabs; the Settings group's own `admin` gate, so no
        // route-permissions exception.
        { path: 'settings/printing', element: <PrintSettingsPage /> },
        { path: 'admin', element: <UsersPage /> },
        { path: 'admin/roles', element: <RolesPage /> },
        // ADM-07 — the customer's own subscription. Owner-only through the
        // FLAG rather than a permission (R18/BR-PERM-69), so
        // route-permissions.ts carries the product's one `ownerOnly` rule.
        // READ-ONLY: R21 gives access exactly two routes, an online payment or
        // an operator grant, and a Switch plan button here would be a third.
        { path: 'admin/billing', element: <SubscriptionBillingPage /> },
        { path: ':groupSlug', element: <GroupPage /> },
      ],
    },
    { path: '*', element: <Navigate to="/" replace /> },
  ]);
}
